/**
 * FieldSelect — multi-select widget for custom formula fields.
 *
 * Shows:
 *   - Selected fields as removable chips
 *   - Dropdown with previously created fields from Firestore
 *   - "+ Create Field" option that opens the FieldModal
 */
import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, Pencil, Trash2, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { toast } from '@/components/ui/Toast';
import { FieldModal } from './FieldModal';

interface EventFilterCondition {
  field: string;
  operator: string;
  value: string;
}

interface CustomField {
  id: string;
  name: string;
  formula: string;
  events: string[];
  eventFilters?: Record<string, EventFilterCondition[]>;
  createdAt: string;
  updatedAt: string;
}

interface FieldSelectProps {
  /** Array of selected field IDs */
  value: string[];
  onChange: (fieldIds: string[]) => void;
  /** Available event names for the formula editor @ picker */
  availableEvents?: string[];
  /** Current table name — needed for per-event property lookup */
  tableName?: string;
  /** Current start date — needed for per-event property lookup */
  startDate?: string;
  /** Current end date — needed for per-event property lookup */
  endDate?: string;
  /** Section scope for Firestore field storage (default: 'product') */
  sectionId?: string;
}

export function FieldSelect({ value, onChange, availableEvents = [], tableName, startDate, endDate, sectionId = 'product' }: FieldSelectProps) {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [modalState, setModalState] = useState<{
    open: boolean;
    editing?: CustomField;
  }>({ open: false });
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch saved fields
  const { data: fieldsData } = useQuery<{ fields: CustomField[] }>({
    queryKey: ['fields', sectionId],
    queryFn: () => api.get<{ fields: CustomField[] }>(endpoints.fields.list(sectionId)),
    staleTime: 30_000,
  });

  const fields = fieldsData?.fields || [];
  const selectedFields = fields.filter((f) => value.includes(f.id));

  const toggleField = (fieldId: string) => {
    if (value.includes(fieldId)) {
      onChange(value.filter((id) => id !== fieldId));
    } else {
      onChange([...value, fieldId]);
    }
  };

  const removeField = (fieldId: string) => {
    onChange(value.filter((id) => id !== fieldId));
  };

  const handleCreate = () => {
    setIsOpen(false);
    setModalState({ open: true });
  };

  const handleEdit = (e: React.MouseEvent, field: CustomField) => {
    e.stopPropagation();
    setIsOpen(false);
    setModalState({ open: true, editing: field });
  };

  const handleDelete = async (e: React.MouseEvent, field: CustomField) => {
    e.stopPropagation();
    if (!window.confirm(`Delete field "${field.name}"?`)) return;

    try {
      await api.delete(endpoints.fields.delete(sectionId, field.id));
      queryClient.invalidateQueries({ queryKey: ['fields', sectionId] });
      if (value.includes(field.id)) {
        onChange(value.filter((id) => id !== field.id));
      }
    } catch {
      toast.error('Failed to delete field');
    }
  };

  const handleModalClose = () => {
    setModalState({ open: false });
  };

  const handleModalSave = (savedField: CustomField) => {
    queryClient.invalidateQueries({ queryKey: ['fields', sectionId] });
    // Auto-select the newly created/edited field
    if (!value.includes(savedField.id)) {
      onChange([...value, savedField.id]);
    }
    setModalState({ open: false });
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <div className="w-full" ref={containerRef}>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">
        Custom Fields
      </label>

      <div className="relative">
        {/* Trigger area */}
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={cn(
            'w-full min-h-[36px] rounded-lg border bg-white px-2.5 py-1.5 text-sm text-left',
            'flex items-center gap-1.5 flex-wrap',
            'transition-colors duration-150 cursor-pointer',
            'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
            isOpen ? 'border-brand-400 ring-2 ring-brand-500/20' : 'border-slate-200 hover:border-slate-300',
          )}
        >
          {selectedFields.length === 0 ? (
            <span className="text-slate-400 py-0.5">Select fields...</span>
          ) : (
            selectedFields.map((field) => (
              <span
                key={field.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand-50 text-brand-700 text-xs font-medium border border-brand-200"
              >
                <span className="truncate max-w-[120px]">{field.name}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeField(field.id);
                  }}
                  className="shrink-0 hover:text-brand-900 transition-colors"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))
          )}
          <ChevronDown className={cn(
            'w-4 h-4 text-slate-400 transition-transform duration-150 ml-auto shrink-0',
            isOpen && 'rotate-180',
          )} />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-30 top-full mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto">
            {/* Saved fields */}
            {fields.length > 0 && (
              <div>
                <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                  Saved Fields
                </div>
                {fields.map((field) => {
                  const isSelected = value.includes(field.id);
                  return (
                    <div
                      key={field.id}
                      onClick={() => toggleField(field.id)}
                      className={cn(
                        'flex items-center justify-between px-3 py-2 cursor-pointer text-sm hover:bg-slate-50 transition-colors group',
                        isSelected && 'bg-brand-50 text-brand-700',
                      )}
                    >
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        {/* Checkbox */}
                        <div className={cn(
                          'w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors',
                          isSelected
                            ? 'bg-brand-500 border-brand-500'
                            : 'border-slate-300 group-hover:border-slate-400',
                        )}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium">{field.name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={(e) => handleEdit(e, field)}
                          className="p-1 rounded hover:bg-slate-200 text-slate-500"
                          title="Edit"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleDelete(e, field)}
                          className="p-1 rounded hover:bg-red-100 text-slate-500 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Create new field */}
            <div className="border-t border-slate-100 mt-1 pt-1">
              <button
                type="button"
                onClick={handleCreate}
                className="w-full px-3 py-2 text-left text-sm text-brand-600 hover:bg-brand-50 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Field
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalState.open && (
        <FieldModal
          editing={modalState.editing}
          onClose={handleModalClose}
          onSave={handleModalSave}
          availableEvents={availableEvents}
          tableName={tableName}
          startDate={startDate}
          endDate={endDate}
          sectionId={sectionId}
        />
      )}
    </div>
  );
}
